import { ChatBubbleIcon, ChevronDownIcon } from "@radix-ui/react-icons";
import { Box, Button, Card, Flex, Heading, Text } from "@radix-ui/themes";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useUsage } from "../../contexts/UsageContext";
import { trackEvent } from "../../utils/posthogConfig";
import { openSupportChat } from "../../utils/supportChat";
import { formatResetDate } from "../../utils/usageMonths";
import { CARD_NO_PADDING } from "../dashboard/tokens";
import { ONE_ACCOUNT_GUIDE_ID, ONE_ACCOUNT_GUIDE_TITLE, OneAccountGuideSteps } from "../shared/OneAccountGuide";
import type { BillingAccessSummary } from "./SubscriptionCard";

type Faq = {
    id: string;
    question: string;
    answer: ReactNode;
};

/**
 * Answers to the billing questions support gets most, so people don't have to leave the page or
 * open a chat for them. Only questions that apply to this account are listed.
 */
export default function BillingHelpCard({ subscription }: { subscription: BillingAccessSummary | null }) {
    const { usage, hasInvoices, loading } = useUsage();
    const [chatUnavailable, setChatUnavailable] = useState(false);
    const { hash, key } = useLocation();
    const guideRef = useRef<HTMLDetailsElement>(null);
    const ready = !loading && usage !== null;

    // A link to the guide (/billing#one-account, from the upgrade check, the Pro hint, the device
    // setup flow or a support reply) opens its toggle and brings it into view. Runs again once the
    // card has loaded, and on every click of such a link, even when the hash is already set.
    useEffect(() => {
        const guide = guideRef.current;
        if (hash !== `#${ONE_ACCOUNT_GUIDE_ID}` || !guide) {
            return;
        }
        guide.open = true;
        // Instant, like a native anchor jump: a smooth scroll is cut short while the cards above
        // are still loading, and the browser's scroll anchoring keeps the guide in place after.
        guide.scrollIntoView({
            block: "start",
        });
        guide.querySelector("summary")?.focus({
            preventScroll: true,
        });
    }, [
        hash,
        key,
        ready,
    ]);

    if (loading || !usage) {
        return null;
    }

    const isPro = usage.plan === "pro";
    const faqs: Faq[] = [];
    if (!isPro && usage.callsIncluded !== null) {
        faqs.push(
            {
                id: "reset",
                question: "When do my free tool calls reset?",
                answer: `They reset once a month. Your next reset is on ${formatResetDate(usage.resetsAt)}.`,
            },
            {
                id: "limit",
                question: "What happens when I reach the limit?",
                answer: "Tool calls stop until your next reset. Pro has no monthly limit, so upgrading lets you continue right away.",
            },
        );
    }
    // Same condition as the Manage row in BillingManageCard.
    const hasManageRow = hasInvoices || subscription?.hasProAccess === true;
    faqs.push({
        id: "company_details",
        question: "How do I add company details or a VAT ID to my invoices?",
        answer: hasManageRow
            ? "Use Manage above to add or change your company name, address and VAT or tax ID. Changes apply to future invoices."
            : 'When you upgrade, tick "I\'m purchasing as a business" at checkout to add your company name and VAT or tax ID.',
    });
    if (subscription?.cancelable) {
        faqs.push({
            id: "cancel",
            question: "How do I cancel, and what happens after?",
            answer: "Use Cancel subscription above. You keep Pro until the end of the period you paid for, then your account moves to the Free plan.",
        });
    }
    // For everyone: paying on one account while ChatGPT or the device uses another is the most
    // expensive billing mistake, and other pages link here.
    faqs.push({
        id: "one_account",
        question: ONE_ACCOUNT_GUIDE_TITLE,
        answer: <OneAccountGuideSteps />,
    });

    const openChat = () => {
        const opened = openSupportChat();
        setChatUnavailable(!opened);
        void trackEvent("support_chat_clicked", {
            surface: "billing_page",
            opened,
        });
    };

    return (
        <Card size="3" mt="5" style={CARD_NO_PADDING}>
            <Box px="5" pt="4" pb="4">
                <Heading size="3">Billing questions</Heading>
            </Box>
            {faqs.map((faq) => (
                <details
                    key={faq.id}
                    id={faq.id === "one_account" ? ONE_ACCOUNT_GUIDE_ID : undefined}
                    ref={faq.id === "one_account" ? guideRef : undefined}
                    className="dc-row dc-faq"
                    onToggle={(event) => {
                        if (event.currentTarget.open) {
                            void trackEvent("billing_faq_opened", {
                                question: faq.id,
                            });
                        }
                    }}
                >
                    <summary>
                        <Text size="2" weight="medium">
                            {faq.question}
                        </Text>
                        <ChevronDownIcon className="dc-faq-chevron" aria-hidden="true" />
                    </summary>
                    {typeof faq.answer === "string" ? (
                        <Text as="p" size="2" color="gray" mt="2">
                            {faq.answer}
                        </Text>
                    ) : (
                        <Box mt="2">{faq.answer}</Box>
                    )}
                </details>
            ))}
            {/* The support chat only loads for Pro accounts (see McpApp), so free users get no button that can't work. */}
            {isPro && (
                <Flex align="center" justify="between" gap="3" wrap="wrap" className="dc-row">
                    <Text
                        as="p"
                        size="2"
                        color="gray"
                        role={chatUnavailable ? "alert" : undefined}
                        style={{
                            flex: "1 1 240px",
                        }}
                    >
                        {chatUnavailable ? "The chat didn't load. Check that your browser isn't blocking it, then try again." : "Still need help?"}
                    </Text>
                    <Button
                        type="button"
                        variant="surface"
                        color="gray"
                        onClick={openChat}
                        style={{
                            flex: "none",
                        }}
                    >
                        <ChatBubbleIcon aria-hidden="true" />
                        Chat with us
                    </Button>
                </Flex>
            )}
        </Card>
    );
}
